/*
 * Copyright (c) 2024 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import { ReactElement, memo, useContext } from 'react';
import { TableCell, Typography, Button, Theme } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { ListTableColumnConfig } from './ListTypes';
import { Link } from 'react-router5';
import { TableTooltip } from '../../StyledComponents/TableTooltip';
import ColorBarCell from './ColorBarCell';
import { DATA_UNAVAILABLE, MIN_VALUE_LEHGTH_FOR_TOOLTIP } from '../../../utils/utils';
import { createListStyles } from './ListStyles';
import { AppAbilityContext } from '../../../accessibility/ability';
import { isNavigationAllowed } from '../../../accessibility/utils';

export interface ListTableRowContentProps<T extends object> {
    itemIndex: number;
    item: T;
    columns: ListTableColumnConfig<T>[];
    getColorBarProps?(status: unknown): { color: string; height: number; tooltipTranslatePrefix: string };
    getColoredValue?(value: unknown, key?: unknown): { color: string; text: string };
    getCustomCellContent?(index: number, columnKey: string, row: number): ReactElement;
    onClick?(columnKey: string, row: number): void;
}

const ListTableRowContent = <T extends object>(props: ListTableRowContentProps<T>) => {
    const ListStyles = makeStyles((theme: Theme) => createStyles(createListStyles(theme)));
    const classes = ListStyles();
    const { columns, item, itemIndex, getColorBarProps, getColoredValue, getCustomCellContent, onClick } = props;
    const ability = useContext(AppAbilityContext);

    const getCellClasses = (index: number): string => {
        if (index === 0) return classes.firstCell;
        else if (index === columns.length - 1) return classes.lastCell;
        else return classes.middleCell;
    };

    const getCellTextClasses = (index: number, value: unknown): string => {
        let classNames = `${classes.tableBodyText}`;

        if (value === DATA_UNAVAILABLE) {
            classNames += ` ${classes.unavailableValue}`;
            return classNames;
        }

        if (columns[index].isBold && value !== '-') classNames += ` ${classes.boldText}`;
        if (columns[index].isClickable) classNames += ` ${classes.clickableText}`;

        return classNames;
    };

    return (
        <>
            {columns.map((column, index) => {
                return column.displayCustomCell && getCustomCellContent ? (
                    getCustomCellContent(index, column.keyForValue as string, itemIndex)
                ) : column.displayAsColorBar ? (
                    <ColorBarCell
                        key={index}
                        displayOptions={getColorBarProps(item[column.keyForValue])}
                        tooltip={column.isWithTooltip ? item[column.keyForValue].toString() : ''}
                    />
                ) : column.isHidden ? null : (
                    <TableTooltip
                        key={index}
                        title={
                            column.isWithTooltip &&
                            item[column.keyForValue].toString().length > MIN_VALUE_LEHGTH_FOR_TOOLTIP
                                ? item[column.keyForValue].toString()
                                : ''
                        }
                        placement="top"
                    >
                        <TableCell
                            className={getCellClasses(index)}
                            align={column.align}
                            style={{
                                minWidth: column.minWidth,
                                maxWidth: column.maxWidth,
                                width: column.width,
                            }}
                        >
                            {column.link &&
                            isNavigationAllowed(ability, column.link.routeId) &&
                            item[column.link.routeParamKeyForValue].toString() !== DATA_UNAVAILABLE &&
                            item[column.link.routeParamKeyForValue].toString() !== '-' ? (
                                <Link
                                    routeName={column.link.routeId}
                                    routeParams={{
                                        [column.link.routeParamName]: item[column.link.routeParamKeyForValue],
                                    }}
                                >
                                    <Typography
                                        className={
                                            getCellTextClasses(index, item[column.keyForValue]) + ` ${classes.linkText}`
                                        }
                                    >
                                        {item[column.keyForValue] as unknown as string}
                                    </Typography>
                                </Link>
                            ) : column.isClickable ? (
                                item[column.keyForValue] ? (
                                    <Button
                                        className={getCellTextClasses(index, item[column.keyForValue])}
                                        variant="text"
                                        onClick={() => onClick(column.keyForValue as string, itemIndex)}
                                    >
                                        {item[column.keyForValue] as unknown as string}
                                    </Button>
                                ) : null
                            ) : (
                                <Typography
                                    className={getCellTextClasses(index, item[column.keyForValue])}
                                    style={{
                                        color: column.displayAsColorText
                                            ? getColoredValue(item[column.keyForValue], column.keyForValue).color
                                            : null,
                                    }}
                                >
                                    {column.displayAsColorText
                                        ? getColoredValue(item[column.keyForValue], column.keyForValue).text
                                        : (item[column.keyForValue] as unknown as string)}
                                </Typography>
                            )}
                        </TableCell>
                    </TableTooltip>
                );
            })}
        </>
    );
};

export default memo(ListTableRowContent);
